import { LocalizationRes, TBaseItem } from "@centricsoftware/design-system";
import { RadioButtonOptionsType } from "@centricsoftware/design-system/components/molecules/Inputs/RadioButton/RadioButton.types";
import { ATTR_NAME, C8NodeType, contextualDataStoreId, globalDataStoreId, VALUE_TYPE } from "c8-platform";
import comm from "csi/comm";
import { REQUIRED_RES } from "csi/components/constants";
import configPreferences from "csi/config/preferences";
import ExtendedPath from "csi/core/ExtendedPath";
import RuleStore from "csi/middleware/attributeRule/RuleStore";
import { createRuleStore } from "csi/middleware/customView/customViewDataManagementService";
import { writeCustomView } from "csi/middleware/customView/customViewSaveService";
import { ICustomViewQueryOptions } from "csi/middleware/customView/options/CustomViewOptions";
import { getClass } from "csi/reflection/reflection";
import { submitWriter } from "csi/service/writerService";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { ILocaleProvider } from "designSystem/locale";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { SyntheticEvent } from "react";

const CONTEXTUAL = "contextual";
const GLOBAL = "global";

export class DataStoreDialogStore {
	readonly loc: ILocaleProvider;
	readonly scopeOptions: RadioButtonOptionsType[];
	readonly sourceClass: C8NodeType;
	readonly targetClass: C8NodeType;

	/** callback to use when closing modal */
	readonly onCancel?: VoidFunction;

	/** callback to use when saving modal */
	private readonly _onSave?: VoidFunction;

	@observable description = "";
	@observable isGlobal = true;
	@observable limitPaths = "";
	@observable name = "";
	@observable pathHints = "";
	@observable.ref ruleStore: RuleStore;
	@observable saveAsGlobal = false;
	@observable switchBackToGlobal = false;
	@observable.ref validationErrorRes?: LocalizationRes;
	/** indicates if required inputs should be validated (typically only after Save clicked) */
	@observable private validateRequired = false;


	/** data store id for entered data */
	@computed get dataStoreId(): string {
		const { name, targetClass, sourceClass, usesSourceClass } = this;

		if (usesSourceClass) {
			return contextualDataStoreId(sourceClass, name, targetClass, true);
		}

		return globalDataStoreId(targetClass, name, true);
	}

	/** indicates an error in at least one of the limit paths */
	@computed get limitPathErrorRes(): LocalizationRes | undefined {
		const { limitPaths, ruleStore, sourceClass, targetClass } = this;

		if (!limitPaths) {
			return;
		}

		const limitPathEntries = limitPaths.split("\n");
		const targetReflectionClass = getClass(targetClass);
		let firstPath: ExtendedPath;

		for (const limitPath of limitPathEntries) {
			const ep = ExtendedPath.create(sourceClass, limitPath);
			if (!ep) {
				return {
					res: "LimitPathNotExists",
					params: [limitPath, sourceClass],
				};
			}

			if (!targetReflectionClass.isDerivedFrom(ep.endType)) {
				return {
					res: "LimitPathWrongTargetClass",
					params: [limitPath, sourceClass, targetClass],
				};
			}

			firstPath ||= ep;
		}

		// if only one path is specified and it starts with a singular path (refs only), and there are no relative query filters,
		// then indicate the source class should change to the class at the end of the singular path
		if ((limitPathEntries.length === 1) && !ruleStore.hasRelativeRule) {
			const { startingSingularPath } = firstPath;
			if (startingSingularPath) {
				return {
					res: "LimitPathWrongSourceClass",
					params: [limitPathEntries[0], startingSingularPath.endType],
				};
			}
		}
	}

	/** options for available path hints */
	get pathHintsOptions(): TBaseItem[] {
		const ids = configPreferences.getAllContextualPathHintsIds().sort();
		return ids.map((id) => ({ id }));
	}

	/** scope value */
	get scope(): string {
		return this.isGlobal ? GLOBAL : CONTEXTUAL;
	}

	/** indicates if data store uses the given source class, either by specifying limit paths or relative filters */
	get usesSourceClass(): boolean {
		return !!this.limitPaths || this.ruleStore.hasRelativeRule;
	}

	/** cancel confirmation dialog to save as global data store */
	@action onCancelSaveAsGlobal = (): void => {
		this.saveAsGlobal = false;
	};

	/** cancel confirmation dialog to switch back to a global data store */
	@action onCancelSwitchBackToGlobal = (): void => {
		this.isGlobal = false;
		this.switchBackToGlobal = false;
	};

	@action onChangeName = (value: string): void => {
		this.name = value;
	};

	@action onChangeDescription = (value: string): void => {
		this.description = value;
	};

	@action onChangeLimitPath = (value: string): void => {
		this.limitPaths = value;
	};	

	@action onChangePathHints = (event: SyntheticEvent, item: TBaseItem): void => {
		this.pathHints = item?.id || "";
	};	

	/** set value for scope */
	@action onChangeScope = (newValue: string): void => {
		const { sourceClass, usesSourceClass } = this;
		const global = (newValue === GLOBAL);
		
		// even though we could be confirming whether or not to make the switch, we still
		// need to change the value so the radio button will be re-rendered if the action is canceled
		this.isGlobal = global;

		if (global && usesSourceClass) {
			this.switchBackToGlobal = true;
		} else {
			this.ruleStore.relativeType = global ? null : sourceClass;
		}

		this.resetValidation();
	};
	
	/** save data store as global */
	@action onContinueSaveAsGlobal = async (): Promise<void> => {
		await this._saveDataStore();
	};

	/** switch back to a global data store */
	@action onContinueSwitchBackToGlobal = (): void => {
		this.switchBackToGlobal = false;
		this.limitPaths = "";

		if (this.ruleStore.hasRelativeRule) {
			this._resetRuleStore();
		}
	};

	/** validate and save data store with current settings if valid */
	@action onSave = async (): Promise<void> => {
		const {
			isGlobal,
			limitPaths,
			limitPathErrorRes,
			name,
			ruleStore,
			usesSourceClass,
			_saveDataStore,
		} = this;

		this.validateRequired = true;

		this.validationErrorRes = undefined;

		if (!name || limitPathErrorRes) {
			return;
		}

		if (!limitPaths && !ruleStore.hasValidRule) {
			this.validationErrorRes = { res: "DataStoreDefinitionEmptyError" };
			return;
		}

		if (!isGlobal && !usesSourceClass) {
			this.saveAsGlobal = true;
		} else {
			await _saveDataStore();
		}
	};

	get nameErrorRes(): LocalizationRes | undefined {
		if (!this.name && this.validateRequired) {
			return REQUIRED_RES;
		}
	}
	
	/** reset validation in cases where new fields are added and don't want to be invalid from the start */
	@action resetValidation = (): void => {
		this.validateRequired = false;
	};

	/** recreate the rule store */
	@action private _resetRuleStore = (): void => {
		this.ruleStore = createRuleStore(this.targetClass, this.sourceClass, this.loc);
	};

	/** persist data store on server */
	private _saveDataStore = async (): Promise<void> => {
		const {
			description,
			limitPaths,
			name,
			pathHints,
			ruleStore,
			sourceClass,
			targetClass,
			usesSourceClass,
			_onSave,
		} = this;

		const writer = new Writer();
		const definitionUrl = comm.createUrl();
		const customViewUrl = comm.createUrl();

		const options: ICustomViewQueryOptions = {
			filterSettings: ruleStore.currentFilterSettings ? { 0: ruleStore.currentFilterSettings } : {},
		};

		const limitPathArray = limitPaths ? limitPaths.split("\n") : [];
			
		writer.createNode(definitionUrl, "DataStoreDefinition", null, xml.NODE_FLAG_NOEDIT)
			.changeAttribute(ATTR_NAME, VALUE_TYPE.STRING, name)
			.changeAttribute("Description", VALUE_TYPE.STRING, description)
			.changeAttribute("SourceClass", VALUE_TYPE.STRING, usesSourceClass ? sourceClass : null)
			.changeAttribute("LimitPaths", VALUE_TYPE.STRING_SET, limitPathArray)
			.changeAttribute("PathHints", VALUE_TYPE.STRING, pathHints);

		const customView = writeCustomView(writer, null, null, options, customViewUrl, targetClass, null, false);
		customView.attach(definitionUrl, "QueryDefinition");

		writer.validateNode(definitionUrl, true);

		try {
			await submitWriter(writer);
			_onSave();
		} catch (ex) {
			runInAction(() => {
				this.saveAsGlobal = false;
				this.validationErrorRes = {
					res: ex.csiErrorCode,
					params: ex.csiErrorParams,
				};
			});
		}
	};

	constructor(sourceClass: C8NodeType, targetClass: C8NodeType, loc: ILocaleProvider, onSave: VoidFunction, onCancel: VoidFunction) {
		this.loc = loc;
		this.onCancel = onCancel;
		this._onSave = onSave;
		this.sourceClass = sourceClass;
		this.targetClass = targetClass;
		this._resetRuleStore();
		this.scopeOptions = [
			{
				labelRes: { res: "DataStoreCreateGlobal" },
				value: GLOBAL,
			},
			{
				labelRes: { res: "DataStoreCreateContextual", params: [loc.type(sourceClass)] },
				helperTextRes: { res:"DataStoreCreateContextualHelper" },
				value: CONTEXTUAL,
			},
		];
	
		makeObservable(this);
	}
}

export default DataStoreDialogStore;